import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  addMonths, subMonths, format, startOfMonth, endOfMonth,
  eachDayOfInterval, getDay, isToday, isSameMonth, parseISO, isValid, isBefore,
} from "date-fns";
import {
  ChevronLeft, ChevronRight, Search, CalendarDays, SunMedium,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LeaveDetailModal } from "./LeaveDetailModal";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import {
  apiGetLeaveCalendar, apiGetLeaveRequest, apiGetMyLeaveRequest,
  type LeaveCalendarRequest,
} from "@/lib/api";
import { useDateLocale } from "@/hooks/useDateLocale";
import { getLeaveStatusLabel } from "@/lib/leave-i18n";

// ── Helpers ───────────────────────────────────────────────────────────────────

function isoDate(d: Date) {
  return format(d, "yyyy-MM-dd");
}

type ChipPos = "single" | "start" | "middle" | "end";

function chipPos(req: LeaveCalendarRequest, day: Date): ChipPos {
  const iso = isoDate(day);
  const isStart = iso === req.startDate;
  const isEnd = iso === req.endDate;
  if (isStart && isEnd) return "single";
  if (isStart) return "start";
  if (isEnd) return "end";
  return "middle";
}

/**
 * Returns true if the avatar/name label should be shown for this request
 * on this calendar day. It's shown on the start day as usual, but ALSO on
 * the first day of the viewed month when the leave started in a prior month
 * (cross-month leave — without this, continuation days in the new month
 * would never show the employee's avatar).
 */
function showChipLabel(req: LeaveCalendarRequest, day: Date, viewMonth: Date): boolean {
  const pos = chipPos(req, day);
  if (pos === "single" || pos === "start") return true;
  // Cross-month continuation: show avatar on the first day of the viewed month
  if (pos === "middle" || pos === "end") {
    const firstOfMonth = startOfMonth(viewMonth);
    if (isoDate(day) === isoDate(firstOfMonth)) {
      const reqStart = parseISO(req.startDate);
      if (isBefore(reqStart, firstOfMonth)) return true;
    }
  }
  return false;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface LeaveCalendarProps {
  scope?: "team" | "mine";
}

export function LeaveCalendar({ scope = "team" }: LeaveCalendarProps) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const today = new Date();

  const DAY_HEADERS = [
    t("leave.dayShort.mon"),
    t("leave.dayShort.tue"),
    t("leave.dayShort.wed"),
    t("leave.dayShort.thu"),
    t("leave.dayShort.fri"),
    t("leave.dayShort.sat"),
    t("leave.dayShort.sun"),
  ];

  const [month, setMonth] = useState<Date>(startOfMonth(today));
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [detailId, setDetailId] = useState<number | null>(null);

  // ── Data ──────────────────────────────────────────────────────────────────

  const { data: calendarData, isLoading } = useQuery({
    queryKey: ["leaveCalendar", scope, month.getFullYear(), month.getMonth() + 1],
    queryFn: () =>
      apiGetLeaveCalendar(
        month.getMonth() + 1,
        month.getFullYear(),
        undefined,
        scope === "mine" ? "mine" : undefined,
      ),
  });

  const { data: detailRequest } = useQuery({
    queryKey: [scope === "mine" ? "myLeaveRequest" : "leaveRequest", detailId],
    queryFn: () =>
      scope === "mine"
        ? apiGetMyLeaveRequest(detailId!)
        : apiGetLeaveRequest(detailId!),
    enabled: detailId !== null,
    staleTime: 0,
    retry: false,
  });

  // ── Derived ───────────────────────────────────────────────────────────────

  const leaveTypes = useMemo(() => {
    if (!calendarData) return [];
    const map = new Map<string, string>();
    for (const r of calendarData.requests) {
      if (!map.has(r.leaveType))
        map.set(r.leaveType, r.leaveTypeLabel || r.leaveType);
    }
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [calendarData]);

  const filtered = useMemo(() => {
    if (!calendarData) return [];
    const q = search.trim().toLowerCase();
    return calendarData.requests.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (typeFilter !== "all" && r.leaveType !== typeFilter) return false;
      if (q && !r.employeeName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [calendarData, statusFilter, typeFilter, search]);

  const holidayMap = useMemo(() => {
    const map = new Map<string, string>();
    if (calendarData) for (const h of calendarData.holidays) map.set(h.date, h.name);
    return map;
  }, [calendarData]);

  // Build 42-cell grid (Mon-first)
  const calendarDays = useMemo<(Date | null)[]>(() => {
    const first = startOfMonth(month);
    const last = endOfMonth(month);
    const days = eachDayOfInterval({ start: first, end: last });
    const startPad = (getDay(first) + 6) % 7; // Mon=0 … Sun=6
    const cells: (Date | null)[] = Array(startPad).fill(null);
    cells.push(...days);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month]);

  // Requests that fall on a given day (after filtering)
  function dayRequests(day: Date): LeaveCalendarRequest[] {
    const iso = isoDate(day);
    return filtered.filter((r) => iso >= r.startDate && iso <= r.endDate);
  }

  // Summary counts for stats strip
  const approvedCount = filtered.filter((r) => r.status === "Approved").length;
  const pendingCount  = filtered.filter((r) => r.status === "Pending").length;
  const uniquePeople  = new Set(filtered.map((r) => r.employeeId)).size;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* ── Month nav + filters ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Month nav */}
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="icon" className="h-8 w-8"
            onClick={() => setMonth((m) => subMonths(m, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-base font-semibold min-w-[150px] text-center select-none">
            {format(month, "MMMM yyyy", { locale: dateLocale })}
          </span>
          <Button variant="outline" size="icon" className="h-8 w-8"
            onClick={() => setMonth((m) => addMonths(m, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground ml-1"
            onClick={() => setMonth(startOfMonth(today))}>
            {t("leave.todayBtn")}
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder={t("leave.searchPerson")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-sm w-44"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 text-sm w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("leave.allStatuses")}</SelectItem>
              <SelectItem value="Approved">{getLeaveStatusLabel(t, "Approved")}</SelectItem>
              <SelectItem value="Pending">{getLeaveStatusLabel(t, "Pending")}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-8 text-sm w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("leave.allLeaveTypes")}</SelectItem>
              {leaveTypes.map((lt) => (
                <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Stats strip ── */}
      {!isLoading && calendarData && (
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm bg-card">
            <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
            <span className="text-muted-foreground">{getLeaveStatusLabel(t, "Approved")}</span>
            <span className="font-semibold tabular-nums">{approvedCount}</span>
          </div>
          <div className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm bg-card">
            <span className="h-2 w-2 rounded-full bg-yellow-400 shrink-0" />
            <span className="text-muted-foreground">{getLeaveStatusLabel(t, "Pending")}</span>
            <span className="font-semibold tabular-nums">{pendingCount}</span>
          </div>
          <div className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm bg-card">
            <CalendarDays className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">{t("leave.peopleOnLeave")}</span>
            <span className="font-semibold tabular-nums">{uniquePeople}</span>
          </div>
          {holidayMap.size > 0 && (
            <div className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm bg-card">
              <SunMedium className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <span className="text-muted-foreground">{t("leave.publicHolidaysCount")}</span>
              <span className="font-semibold tabular-nums">{holidayMap.size}</span>
            </div>
          )}
        </div>
      )}

      {/* ── Calendar grid ── */}
      {isLoading ? (
        <div className="h-[520px] rounded-xl border bg-muted/20 animate-pulse" />
      ) : (
        <div className="rounded-xl border overflow-hidden shadow-sm">
          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 border-b bg-muted/40">
            {DAY_HEADERS.map((d) => (
              <div key={d}
                className="py-2.5 text-center text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">
                {d}
              </div>
            ))}
          </div>

          {/* Cells */}
          <div className="grid grid-cols-7">
            {calendarDays.map((day, idx) => {
              // Empty padding cells
              if (!day) {
                return (
                  <div key={`pad-${idx}`}
                    className="min-h-[110px] border-r border-b last-in-row:border-r-0 bg-muted/10" />
                );
              }

              const reqs      = dayRequests(day);
              const holiday   = holidayMap.get(isoDate(day));
              const inMonth   = isSameMonth(day, month);
              const todayCell = isToday(day);
              const dow       = getDay(day); // 0=Sun…6=Sat
              const weekend   = dow === 0 || dow === 6;

              return (
                <div key={isoDate(day)}
                  className={[
                    "min-h-[110px] border-r border-b flex flex-col overflow-hidden",
                    !inMonth     ? "bg-muted/10"  : "",
                    weekend      ? "bg-muted/[0.04]" : "",
                  ].join(" ")}
                >
                  {/* Date number */}
                  <div className="px-2 pt-1.5 pb-0.5 flex items-center justify-between">
                    <span className={[
                      "inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium select-none",
                      todayCell
                        ? "bg-primary text-primary-foreground font-bold"
                        : !inMonth
                        ? "text-muted-foreground/30"
                        : weekend
                        ? "text-muted-foreground"
                        : "text-foreground",
                    ].join(" ")}>
                      {format(day, "d")}
                    </span>
                  </div>

                  {/* Holiday banner */}
                  {holiday && (
                    <div className="mx-1 mb-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium leading-tight
                      bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 truncate">
                      <SunMedium className="inline h-2.5 w-2.5 mr-0.5 -mt-px" />
                      {holiday}
                    </div>
                  )}

                  {/* Leave chips */}
                  <div className="flex flex-col gap-0.5 px-1 pb-1.5 flex-1">
                    {reqs.slice(0, 4).map((req) => {
                      const pos   = chipPos(req, day);
                      const color = req.leaveTypeColor || (req.status === "Approved" ? "#22c55e" : "#f59e0b");
                      const pend  = req.status === "Pending";
                      const showLabel = showChipLabel(req, day, month);

                      // Margin/rounding per span position.
                      // When showLabel is true but pos is "middle" (cross-month
                      // continuation), render as a start chip so the avatar sits
                      // flush against the left border.
                      const effectivePos = showLabel && pos === "middle" ? "start" : pos;
                      const posClass =
                        effectivePos === "single" ? "mx-0.5 rounded"    :
                        effectivePos === "start"  ? "ml-0.5 mr-0 rounded-l rounded-r-none" :
                        effectivePos === "end"    ? "ml-0 mr-0.5 rounded-r rounded-l-none" :
                                                    "mx-0 rounded-none";

                      return (
                        <button
                          key={`${req.id}-${isoDate(day)}`}
                          onClick={() => setDetailId(req.id)}
                          className={`group flex items-center gap-1 overflow-hidden min-h-[22px] py-0.5
                            hover:brightness-95 transition-all cursor-pointer ${posClass}`}
                          style={{
                            backgroundColor: `${color}${pend ? "1a" : "28"}`,
                            borderLeft: (effectivePos === "single" || effectivePos === "start")
                              ? `3px solid ${color}` : "none",
                            outline: pend ? `1.5px dashed ${color}66` : "none",
                            outlineOffset: "-1px",
                          }}
                          title={showLabel
                            ? t("leave.calendarEntryTitle", {
                                employee: req.employeeName,
                                type: t(`leave.types.${req.leaveType}`, {
                                  defaultValue: req.leaveTypeLabel || req.leaveType,
                                }),
                                status: getLeaveStatusLabel(t, req.status),
                              })
                            : undefined}
                        >
                          {showLabel ? (
                            <>
                              <EmployeeAvatar
                                avatarUrl={req.employeeAvatarUrl}
                                avatarColor={req.employeeAvatarColor}
                                initials={req.employeeInitials}
                                name={req.employeeName}
                                size="sm"
                                className="h-4 w-4 text-[7px] shrink-0 ml-0.5"
                              />
                              <span className="text-[11px] font-medium leading-none truncate pr-1"
                                style={{ color }}>
                                {req.employeeName.split(" ")[0]}
                              </span>
                            </>
                          ) : (
                            // Middle/end: just a color continuation bar
                            <span className="flex-1 h-full" />
                          )}
                        </button>
                      );
                    })}

                    {reqs.length > 4 && (
                      <span className="text-[10px] text-muted-foreground px-1.5 leading-tight">
                        {t("leave.moreEntries", { count: reqs.length - 4 })}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Legend ── */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-emerald-500/30 border-l-2 border-emerald-500" />
          {t("leave.approvedLeaveLabel")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-yellow-400/20 border border-dashed border-yellow-500/60" />
          {t("leave.pendingApprovalLabel")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-amber-100 border border-amber-300" />
          {t("leave.publicHoliday")}
        </span>
        <span className="ml-auto flex items-center gap-1 opacity-70">
          <CalendarDays className="h-3 w-3" />
          {t("leave.calClickToView")}
        </span>
      </div>

      {/* ── Detail modal ── */}
      <LeaveDetailModal
        request={detailRequest ?? null}
        open={detailId !== null}
        onOpenChange={(v) => { if (!v) setDetailId(null); }}
      />
    </div>
  );
}
